import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import Modal, { useModal } from '../../Components/Modal';
import { Button } from '../../Components/Buttons';
import { TextInput } from '../../Components/Inputs/TextInput';
import { useRefresh } from '../../Hooks/useRefresh';
import { PencilIcon, TrashIcon } from '@heroicons/react/20/solid';
import ClearAll from '../../Resources/Images/Files/ClearAll';
import Select from '../../Components/Inputs/Select';
import Badge, { badgeColorsChoices } from '../../Components/Badge';
import { useContactMappingForTable } from '../Contacts/Contacts';
import { TagModal } from './Tags';
import HeaderWithBreadcrumb from '../../Components/Headers/HeaderWithBreadcrumb';
import StepProgressBar from '../../Components/ProgressBar/StepProgressBar';

export default function Tag() {

    const { translate, language, setLanguage } = useTranslation();
    const navigate = useNavigate();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();

    const [tag, setTag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contacts, setContacts] = useState([]);
    const pagination = usePaginations();
    const { open, setOpen } = useModal();
    const { open: openImportContacts, setOpen: setOpenImportContacts } = useModal();
    const { refreshId, refresh } = useRefresh();
    const { page, pageSize } = pagination;
    const [selectedTag, setSelectedTag] = useState(null);
    const tagId = useParams().tagId;

    useEffect(() => {
        const fetchTag = async () => {
            try {
                setLoading(true);
                const response = await api.getTag(tagId, page, pageSize);
                setTag(response.data.tag);
                setContacts(response.data.contacts);
                pagination.setMetaData(response.data.meta);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchTag();
    }, [pagination.page, refreshId]);

    const mappedContacts = useContactMappingForTable(contacts);

    const handleEdit = (tag) => {
        try {
            setOpen(true);
        } catch (error) {
            console.error(error);
        }
    }

    const handleDelete = async (contact) => {
        try {
            await api.bulkRemoveContactFromTag(tagId, [contact.id]);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const pages = useMemo(() => [
        { name: 'Tags', href: '/tags', current: false },
        { name: tag?.name, href: '#', current: true },
    ], [tag]);

    return (
        <>
            {/* Place the Modal outside the main container */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={tag ? translate('tag.edit') : translate('tag.create')}
                description={''}
                children={<TagModal refresh={refresh} setOpen={setOpen} tag={tag} setSelectedTag={setSelectedTag} open={open} />}
            />

            <Modal
                maxWidth="sm:max-w-2xl"
                open={openImportContacts}
                onClose={() => setOpenImportContacts(false)}
                title={translate('tag.import_contacts')}
                description={''}
                children={<ImportContactsModal open={openImportContacts} setOpen={setOpenImportContacts} tagId={tagId} refresh={refresh} />}
            />

            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="tags">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <>
                            <div className="mb-2">
                                <Badge color={tag.color} text={tag.name} withoutRemove={true} />
                            </div>
                            <HeaderWithBreadcrumb pages={pages} title={tag.name} actions={[]} />
                            <div className="mt-8">
                                <Table
                                    title={translate('tags.contacts_title')}
                                    description={translate('tags.contacts_description')}
                                    data={mappedContacts}
                                    translationKey="contacts.table"
                                    columns={['external_id']}
                                    pagination={pagination}
                                    actions={[{
                                        text: translate('tag.import_contacts'),
                                        action: () => setOpenImportContacts(true)
                                    }]}
                                    createButton={{
                                        text: translate('tags.edit'),
                                        action: () => setOpen(true)
                                    }}
                                    onRowClick={(row) => {
                                        navigate(`/contacts/${row.external_id}`);
                                    }}
                                    dropdownGroups={[{
                                        options: [{
                                            label: translate('tag.delete'),
                                            icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-gray-500" />,
                                            action: (row) => handleDelete(row)
                                        }]
                                    }]}
                                />
                            </div>
                        </>
                    )}
                </SidebarNav>
            </div>
        </>
    );
}

export const ImportContactsModal = ({ open, setOpen, tagId, refresh }) => {
    const { translate } = useTranslation();

    const [fields, setFields] = useState([]);
    const [contactData, setContactData] = useState([]);


    const [currentStep, setCurrentStep] = useState(1);


    const getStatus = (step) => {
        if (step < currentStep) {
            return 'complete';
        } else if (step === currentStep) {
            return 'current';
        } else {
            return 'upcoming';
        }
    }

    const steps = [
        { id: 1, name: translate('tag.import_contacts'), status: getStatus(1) },
        { id: 2, name: translate('tag.map_import_field'), status: getStatus(2) },
        { id: 3, name: translate('tag.add_tag_import'), status: getStatus(3) },
      ]

    return (
        <div className="flex flex-col w-full">
            <StepProgressBar steps={steps} currentStep={currentStep} setCurrentStep={setCurrentStep} />
            <div className="flex flex-col mt-8">
                <div className={`flex flex-col ${currentStep === 1 ? 'visible' : 'hidden'}`}>
                    <ImportFileStep open={open} setOpen={setOpen} tagId={tagId} refresh={refresh} setCurrentStep={setCurrentStep} setFields={setFields} setContactData={setContactData} fields={fields} contactData={contactData} />
                </div>
                <div className={`flex flex-col ${currentStep === 2 ? 'visible' : 'hidden'}`}>
                    <MapImportFieldStep open={open} setOpen={setOpen} tagId={tagId} refresh={refresh} setCurrentStep={setCurrentStep} setFields={setFields} setContactData={setContactData} fields={fields} contactData={contactData} />
                </div>
                <div className={`flex flex-col ${currentStep === 3 ? 'visible' : 'hidden'}`}>
                    <AddTagImportStep open={open} setOpen={setOpen} tagId={tagId} refresh={refresh} setCurrentStep={setCurrentStep} setFields={setFields} setContactData={setContactData} fields={fields} contactData={contactData} />
                </div>
            </div>
            
        </div>
    )
}

const ImportFileStep = ({ open, setOpen, tagId, refresh, setCurrentStep, setFields, setContactData, fields }) => {
    const { translate } = useTranslation();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const api = useAPI();

    const dispatch = useAppDispatch();

    const extractContactIdsFromCsv = (file) => {
        const reader = new FileReader();

        const parseCSVLine = (line) => {
            const result = [];
            let field = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                
                if (char === '"') {
                    if (inQuotes && line[i + 1] === '"') {
                        // Handle escaped quotes
                        field += '"';
                        i++;
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (char === ',' && !inQuotes) {
                    result.push(field.trim());
                    field = '';
                } else {
                    field += char;
                }
            }
            
            result.push(field.trim());
            return result;
        };

        reader.onload = (e) => {
            try {
                setLoading(true);
                const text = e.target.result;
                const lines = text.split('\n').filter(line => line.trim());
                const firstLine = lines[0];
                const fields = parseCSVLine(firstLine).filter(field => field !== '').map(field => {
                    return {
                        fileName: field,
                        variable_name: undefined
                    }
                });

                const data = lines.slice(1).map(line => parseCSVLine(line));
                setFields(fields);
                setContactData(data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }
        reader.readAsText(file);
    }

    useEffect(() => {
        if (!open) {
            setFile(null);
        }
    }, [open]);

    return (
        <div>
            {loading && <div className="flex justify-center items-center h-full w-12"><LoadingCircleTransparent /></div>}
            <input type="file" accept=".csv" onChange={(e) => extractContactIdsFromCsv(e.target.files[0])} />
            <div className="flex flex-row gap-x-4 justify-end mt-4">
                <Button disabled={loading || !fields.length} onClick={() => setCurrentStep(2)}>{translate('next')}</Button>
            </div>
            
        </div>
    )
}

const MapImportFieldStep = ({ open, setOpen, tagId, refresh, setCurrentStep, setFields, setContactData, fields, contactData }) => {
    const { translate } = useTranslation();

    const api = useAPI();

    const [customFields, setCustomFields] = useState([]);

    useEffect(() => {
        const fetchCustomFields = async () => {
            const response = await api.listCustomFields(1, 9999);
            const defaultFields = [{ id: -1, name: 'tag.external_id', isContactExternalId: true, variable_name: 'external_id' }];
            const otherField = { id: -3, name: 'tag.other_fields' };
            setCustomFields([...defaultFields, ...response.data.customFields.map(field => ({ ...field, name: field.label})), otherField]);
        }
        fetchCustomFields();
    }, []);

    const handleSelectMapField = (customField, fileField) => {
        if (fileField.selected_custom_field && fileField.selected_custom_field.id === customField.id) {
            fileField.selected_custom_field = null;
        } else {
            fileField.variable_name = customField.variable_name;
            fileField.selected_custom_field = customField;
        }
        setFields([...fields]);
    }

    const handleTextInputChange = (fileField, value) => {
        fileField.variable_name = value;
        setFields([...fields]);
    }

    const requiredFields = fields.filter(field => field.selected_custom_field?.isContactExternalId);
    const haveRequiredFields = requiredFields.length === 1;

    return (
        <div>
            <ul className='flex flex-col border border-gray-300 rounded-md p-4'>
                {fields.map((fileField, index) => (
                    <li key={index} className="flex flex-row gap-x-4 justify-between h-12 even:bg-gray-50">
                        <div className='w-24 flex flex-col justify-center'>
                            <span className='text-sm font-medium text-gray-900'>{fileField.fileName}</span>
                        </div>
                        
                        {/* <div className='w-0.5 h-full bg-gray-300' /> */}
                        {/* <TextInput className='flex-1 flex w-full' value={field.variableName} onChange={(e) => setFields(prevFields => prevFields.map((f, i) => i === index ? { ...f, variableName: e.target.value } : f))} /> */}
                        <Select minWidth="min-w-48" options={customFields} selected={fileField.selected_custom_field} setSelected={(customField) => handleSelectMapField(customField, fileField)} />
                        {fileField.selected_custom_field?.id === -3 && <TextInput className='flex-1 flex w-full' value={fileField.variable_name} onChange={(e) => handleTextInputChange(fileField, e.target.value)} />}
                    </li>
                ))}
            </ul>
            <div className="flex flex-row gap-x-4 justify-end mt-4">
                <Button disabled={!haveRequiredFields} onClick={() => setCurrentStep(3)}>{translate('next')}</Button>
            </div>
        </div>
    )
}

const AddTagImportStep = ({ open, setOpen, tagId, refresh, setCurrentStep, setFields, setContactData, fields, contactData }) => {
    const { translate } = useTranslation();
    const api = useAPI();
    const dispatch = useAppDispatch();
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        const fetchTags = async () => {
            const response = await api.listTags(1, 9999);
            setTags(response.data.tags);
        }
        fetchTags();
    }, []);

    const handleSelectTag = (tag) => {
        if (selectedTags.includes(tag)) {
        
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    }

    const removeTag = (tag) => {
        setSelectedTags(selectedTags.filter(t => t !== tag));
    }

    const importContacts = async () => {
        try {
            setLoading(true);
            const data = {
                fields,
                contacts: contactData,
                tags: selectedTags
            }
            
            const response = await api.importContacts(data);
            refresh();
            setOpen(false);
            setCurrentStep(1);
            setFields([]);
            setContactData([]);

            dispatch({ 
                type: ActionTypes.SET_NOTIFICATION, 
                payload: { 
                    show: true, 
                    message: translate('tag.import_contacts_success').replace('%1$d', response.data.new_contacts_count).replace('%2$d', response.data.updated_contacts_count), 
                    type: 'success', 
                    title: translate('tag.success') 
                } 
            });

        } catch (error) {
            
            if (error.message && error.message.includes('timeout')) {

                setOpen(false);
                setCurrentStep(1);
                setFields([]);
                setContactData([]);
                dispatch({ 
                    type: ActionTypes.SET_NOTIFICATION, 
                    payload: { 
                        show: true, 
                        message: translate('tag.import_contacts_timeout'),
                        type: 'warning',
                        title: translate('tag.warning')
                    } 
                });
            }
            
            
        } finally {
            setLoading(false);
        }
    }

    return (
        <div>
            {loading && <div className="flex justify-center items-center h-full w-12"><LoadingCircleTransparent /></div>}
            <Select title={translate('tag.add_tag')} options={tags} selected={undefined} setSelected={handleSelectTag} />
            <div className="flex flex-row gap-4 flex-wrap mt-4">
                {selectedTags.map((tag, index) => (
                    <div key={index}>
                        <Badge onClickRemove={() => {
                            removeTag(tag);
                        }} text={tag.name} color={tag.color} />
                    </div>
                  
                ))}
            </div>
            <div className="flex flex-row gap-x-4 justify-end mt-4">
                <Button disabled={loading} onClick={importContacts}>{translate('tag.import_contacts')}</Button>
            </div>
        </div>
        
    )
}